// „Meine Wallets" — Adress-Registry im Profil, mit Logo-Wizard.
//
// Liste + großes „+" → 3-Schritt-Wizard mit sichtbaren Slide-Übergängen (framer-motion)
// und Press-Feedback auf JEDEM Button (eigener Effekt, unabhängig von reduced-motion):
//   1) Wallet wählen (Brand-Logos via ExchangeLogo)
//   2) Chain wählen (echte Chain-Logos via TrustWallet-Assets, Fallback = Brand-Kreis)
//   3) Adresse einfügen (xpub nur bei UTXO/Bitcoin-Chains, klar erklärt)

import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { F, R } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import InfoBanner from './InfoBanner';
import ExchangeLogo from './ExchangeLogo';
import ChainLogo from './ChainLogo';
import Tap from './Tap';
import { CHAINS } from './ChainWalletConnect';
import { HW_WALLETS, SW_WALLETS } from '../utils/exchanges';
import { listWallets, addWallet, deleteWallet, setWalletWatch } from '../services/walletRegistry';
import { listCandidates, clearCandidates } from '../services/walletCandidates';
import CoinImage from './CoinImage';
import TypeBadge from './red/TypeBadge';
import ImportReviewScreen from '../screens/ImportReviewScreen';
import { priceWalletRows } from '../utils/walletPricing';

const VALUE_RE = /^[a-zA-Z0-9:_-]{20,140}$/;
const UTXO = new Set(['BTC', 'LTC', 'DOGE', 'BCH']);              // hier ist xpub sinnvoll
const SLIDE = { initial: { opacity: 0, x: 22 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -22 }, transition: { duration: 0.26, ease: [0.16, 1, 0.3, 1] } };
// Gestaffeltes Einfaden der Inhalte beim Öffnen (kein statischer Auftritt mehr).
const STAGGER = { hidden: {}, show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } } };
const ITEM = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.42, ease: [0.16, 1, 0.3, 1] } } };

// Anbieter eines Wallets bestimmen (für Gruppierung + Icon). Reihenfolge:
//   gespeichertes provider-Feld (aus der Import-Quelle gesetzt) >
//   bekannte Marke im Label (Phantom/MetaMask/Ledger…) >
//   xpub-Adresse ⇒ Ledger (Hardware) > 'Andere'.
// Das provider-Feld ist die Wahrheit (wird beim Import aus der Quelle gesetzt);
// die Heuristik fängt Bestandsdaten ohne provider ab, bis sie neu importiert werden.
const PROVIDER_OTHER = 'Andere';
function deriveProvider(w) {
  if (w?.provider) return w.provider;
  const labelLc = String(w?.label || '').toLowerCase();
  const hit = [...HW_WALLETS, ...SW_WALLETS].find(b => labelLc.includes(String(b.label).toLowerCase()));
  if (hit) return hit.label;
  if (/^(xpub|ypub|zpub|vpub|upub|Ltub|Mtub|dgub|drkp)/.test(String(w?.address || ''))) return 'Ledger';
  return PROVIDER_OTHER;
}

export default function WalletRegistry({ uid, onBack, onImport, txs = [], marketData = {} }) {
  const { T } = useTheme();
  const { t } = useT();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openGroups, setOpenGroups] = useState({}); // Anbieter-Gruppen auf/zu
  const [candidates, setCandidates] = useState([]);   // „Zu prüfen" (Watch-Cron)
  const [reviewResult, setReviewResult] = useState(null); // öffnet ImportReviewScreen
  const [reviewLoading, setReviewLoading] = useState(false); // Preise werden geladen

  // Native App: /app/wallets ist eine Ionic-Route mit nativem Swipe-back → der
  // Wurzel-Pfeil (Exit zum Profil) ist redundant und wird ausgeblendet. Der
  // Schritt-Pfeil (address→chain→wallet) bleibt, da er KEIN Route-Exit ist.
  const isNativeApp = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();

  const [step, setStep] = useState(null); // null=Liste | 'wallet' | 'chain' | 'address'
  const [draft, setDraft] = useState({ label: '', chain: '', address: '' });
  const [otherName, setOtherName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try { const list = await listWallets(uid); if (alive) setWallets(list); }
      catch { /* leer */ }
      try { const cands = await listCandidates(uid); if (alive) setCandidates(cands); }
      catch { /* leer */ }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [uid]);

  // Hinweis: Die automatische Übernahme eindeutiger Eigen-Transfers läuft jetzt
  // app-weit beim Start (App.js → autoImportOwnTransferCandidates), nicht mehr
  // hier. Dieser Screen zeigt nur die verbleibenden „Zu prüfen"-Kandidaten.

  const chainOf = (c) => CHAINS.find(x => x.coin === c) || {};

  // Chain-Bezeichner (Symbol ODER Voll-Name, beliebige Schreibweise) → Coin-Symbol.
  // Wallet-Chains kommen gemischt rein: 'btc'/'xrp'/'hbar' (Symbol, lowercase),
  // 'ethereum'/'solana'/'algorand' (Voll-Name aus Ledger-„Account Name"), 'SOL'
  // (Connect-Flow). Ohne Auflösung zeigt CoinImage einen Buchstaben-Chip statt des
  // echten farbigen Logos. ALGO/HBAR fehlen in CHAINS → Fallback-Map nötig.
  const CHAIN_NAME_SYM = {
    bitcoin: 'BTC', litecoin: 'LTC', dogecoin: 'DOGE', 'bitcoin cash': 'BCH',
    solana: 'SOL', stellar: 'XLM', ripple: 'XRP', cardano: 'ADA', ethereum: 'ETH',
    base: 'ETH', polygon: 'MATIC', arbitrum: 'ETH', optimism: 'ETH', 'bnb chain': 'BNB',
    algorand: 'ALGO', hedera: 'HBAR', hbar: 'HBAR', tron: 'TRX', avalanche: 'AVAX',
  };
  const chainSym = (c) => {
    const raw = String(c || '').trim();
    if (!raw) return '';
    const up = raw.toUpperCase();
    if (CHAINS.some(x => x.coin === up)) return up;                       // bereits Symbol
    const byName = CHAINS.find(x => String(x.name).toLowerCase() === raw.toLowerCase());
    if (byName) return byName.coin;                                       // Voll-Name in CHAINS
    return CHAIN_NAME_SYM[raw.toLowerCase()] || up;                       // Fallback-Map / roh
  };

  const chainLogo = (coin, size) => <ChainLogo coin={coin} size={size} color={chainOf(coin).color || T.text} />;

  // Anbieter aus der IMPORT-QUELLE der eigenen Transaktionen ableiten — heilt
  // Bestands-Wallets ohne provider-Feld dauerhaft (Display-Layer, kein DB-Write).
  // Coin-Symbol → Wallet-Marke, ABER nur Quellen, die eine bekannte Wallet-Marke
  // sind (Ledger, Phantom, …). Börsen (Binance, Bitpanda) sind KEINE Wallet-
  // Anbieter und werden ignoriert. So landet z.B. die alte „XRP Florian"-Wallet
  // (provider-Feld fehlt, Adresse ist kein xpub) über ihre Ledger-CSV-TXs korrekt
  // unter „Ledger" statt „Andere".
  const walletBrands = useMemo(
    () => new Set([...HW_WALLETS, ...SW_WALLETS].map(b => String(b.label).toLowerCase())),
    [],
  );
  const txProvByCoin = useMemo(() => {
    const m = {};
    for (const tx of (txs || [])) {
      const ex = String(tx.exchange || '').trim();
      if (!ex || !walletBrands.has(ex.toLowerCase())) continue;
      for (const s of [tx.coin, tx.sym, tx.symIn, tx.symOut]) {
        if (s) { const k = String(s).toUpperCase(); if (!m[k]) m[k] = ex; }
      }
    }
    return m;
  }, [txs, walletBrands]);
  // Display-Anbieter: statische Heuristik (provider-Feld > Label-Marke > xpub) und,
  // nur falls die in „Andere" fällt, die TX-Quelle als Rückfall.
  const providerOf = (w) => {
    const p = deriveProvider(w);
    if (p !== PROVIDER_OTHER) return p;
    return txProvByCoin[chainSym(w.chain)] || PROVIDER_OTHER;
  };

  const openWizard = () => { setDraft({ label: '', chain: '', address: '' }); setOtherName(''); setError(null); setStep('wallet'); };
  const pickBrand = (label) => { setDraft(d => ({ ...d, label })); setStep('chain'); };
  const pickChain = (chain) => { setDraft(d => ({ ...d, chain })); setError(null); setStep('address'); };

  const addrOk = VALUE_RE.test(draft.address.trim());
  const save = async () => {
    setError(null);
    if (!draft.label.trim() || !draft.chain || !addrOk) { setError(t('wr_invalid')); return; }
    setBusy(true);
    try {
      const id = await addWallet(uid, draft);
      setWallets(prev => [...prev, { id, ...draft }].sort((a, b) => String(a.label).localeCompare(String(b.label))));
      setStep(null);
    } catch (e) { setError(e.message || t('wr_failed')); }
    finally { setBusy(false); }
  };
  const handleDelete = async (id) => {
    setWallets(prev => prev.filter(w => w.id !== id));
    try { await deleteWallet(uid, id); } catch { /* egal */ }
  };

  // Watch-Flags umschalten (optimistisch, mit Rollback bei Fehler).
  const toggleWatch = async (id, key) => {
    const cur = wallets.find(w => w.id === id);
    if (!cur) return;
    const next = !cur[key];
    setWallets(prev => prev.map(w => (w.id === id ? { ...w, [key]: next } : w)));
    try { await setWalletWatch(uid, id, { [key]: next }); }
    catch { setWallets(prev => prev.map(w => (w.id === id ? { ...w, [key]: cur[key] } : w))); }
  };

  // „Zu prüfen" öffnen: Kandidaten in die kanonische Zeilenform bringen (Chain-Hash
  // als `hash` für Dedup/Konflikt-Erkennung) und den vorhandenen ImportReviewScreen
  // öffnen. Geschrieben wird erst dort über onImport (= App.handleImportCsv → addBatch).
  const openReview = async () => {
    if (!candidates.length || reviewLoading) return;
    const candIds = candidates.map(c => c.id);
    const rawRows = candidates.map(c => ({ ...(c.tx || {}) }));
    setReviewLoading(true);
    let transactions;
    try {
      // Historische Preise nachladen, damit Cost-Basis stimmt (sonst preis:0).
      transactions = await priceWalletRows(rawRows);
    } catch {
      transactions = rawRows.map(tx => ({ ...tx, hash: tx.hash || tx.txHash || '' }));
    } finally {
      setReviewLoading(false);
    }
    setReviewResult({ transactions, exchange: t('wr_review_src'), deterministic: true, _candIds: candIds });
  };

  const PAGE = { padding: '18px 16px 40px', maxWidth: 560, margin: '0 auto' };
  const inputBase = {
    width: '100%', padding: '14px 15px', borderRadius: R.input,
    border: `1px solid ${T.line || T.border}`, background: T.surface, color: T.text,
    fontFamily: F.family, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };
  const H1 = { fontFamily: F.display, fontSize: 25, fontWeight: 700, letterSpacing: '-0.035em', color: T.text, lineHeight: 1.05 };
  const SUB = { fontSize: 13.5, color: T.sub, fontFamily: F.family, fontWeight: 500, lineHeight: 1.5 };
  const SECTION = { fontSize: 11, fontWeight: 700, color: T.mute || T.sub, letterSpacing: '0.09em', textTransform: 'uppercase', fontFamily: F.family };

  const back = () => {
    if (step === 'address') setStep('chain');
    else if (step === 'chain') setStep('wallet');
    else if (step === 'wallet') setStep(null);
    else onBack?.();
  };
  const title = step === 'wallet' ? t('wr_step_wallet')
    : step === 'chain' ? t('wr_step_chain')
    : step === 'address' ? t('wr_step_address')
    : t('wr_title');

  const Grid = ({ children }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 10 }}>{children}</div>
  );
  const Tile = ({ onClick, logo, label, active }) => (
    <Tap onClick={onClick} scale={0.94} style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 9,
      padding: '16px 8px', borderRadius: R.md, minHeight: 104,
      border: `1.5px solid ${active ? T.text : (T.line || T.border)}`, background: T.surface, color: T.text,
    }}>
      {logo}
      <span style={{ fontSize: 12.5, fontWeight: 600, fontFamily: F.family, letterSpacing: '-0.01em', textAlign: 'center', lineHeight: 1.2 }}>{label}</span>
    </Tap>
  );

  // Calm-Toggle: neutraler Track, grün = an. Press-Squish, kein Glow.
  const Toggle = ({ on, onClick, label, sub }) => (
    <Tap onClick={onClick} scale={1} hoverScale={1} style={{
      display: 'flex', alignItems: 'center', gap: 11, width: '100%',
      background: 'none', border: 'none', padding: 0, textAlign: 'left', color: T.text,
    }}>
      <span style={{
        width: 38, height: 22, borderRadius: 999, flexShrink: 0, position: 'relative',
        background: on ? (T.gain || T.green) : (T.line || T.border), transition: 'background .2s',
      }}>
        <span style={{
          position: 'absolute', top: 3, left: on ? 19 : 3, width: 16, height: 16,
          borderRadius: '50%', background: '#fff', transition: 'left .2s',
        }} />
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: on ? T.text : T.sub, fontFamily: F.family, letterSpacing: '-0.01em' }}>{label}</span>
        {sub && <span style={{ display: 'block', fontSize: 10.5, color: T.mute || T.sub, fontFamily: F.family, marginTop: 1 }}>{sub}</span>}
      </span>
    </Tap>
  );

  // ── Schritt-Inhalte ──
  let body;
  if (step === 'wallet') {
    const sec = (txt) => <div style={{ ...SECTION, margin: '22px 0 11px' }}>{txt}</div>;
    body = (
      <>
        <div style={SUB}>{t('wr_step_wallet_sub')}</div>
        {sec(t('wr_hw'))}
        <Grid>{HW_WALLETS.map(w => <Tile key={w.id} onClick={() => pickBrand(w.label)} label={w.label} logo={<ExchangeLogo name={w.label} size={40} />} />)}</Grid>
        {sec(t('wr_sw'))}
        <Grid>{SW_WALLETS.map(w => <Tile key={w.id} onClick={() => pickBrand(w.label)} label={w.label} logo={<ExchangeLogo name={w.label} size={40} />} />)}</Grid>
        {sec(t('wr_other_sec'))}
        <input value={otherName} onChange={e => setOtherName(e.target.value.slice(0, 40))} placeholder={t('wr_other_name_ph')} style={inputBase} />
        <Tap onClick={() => otherName.trim() && pickBrand(otherName.trim())} disabled={!otherName.trim()} style={{
          marginTop: 10, width: '100%', padding: '13px', background: otherName.trim() ? T.redGradient : T.line,
          color: T.white, border: 'none', borderRadius: R.md, fontFamily: F.family, fontSize: 14, fontWeight: 700,
        }}>{t('wr_next')}</Tap>
      </>
    );
  } else if (step === 'chain') {
    body = (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16 }}>
          <ExchangeLogo name={draft.label} size={22} />
          <span style={SUB}>{t('wr_step_chain_sub', { wallet: draft.label })}</span>
        </div>
        <Grid>
          {CHAINS.map(c => <Tile key={c.coin} onClick={() => pickChain(c.coin)} label={c.name} logo={chainLogo(c.coin, 40)} active={draft.chain === c.coin} />)}
        </Grid>
      </>
    );
  } else if (step === 'address') {
    const isUtxo = UTXO.has(draft.chain);
    body = (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, padding: '13px 15px', background: T.surface, border: `1px solid ${T.line || T.border}`, borderRadius: R.md }}>
          <ExchangeLogo name={draft.label} size={34} />
          <div style={{ fontSize: 14.5, fontWeight: 700, color: T.text, fontFamily: F.family, letterSpacing: '-0.01em' }}>
            {draft.label} <span style={{ fontSize: 11.5, fontWeight: 600, color: T.sub }}>· {chainOf(draft.chain).name || draft.chain}</span>
          </div>
          <div style={{ marginLeft: 'auto', lineHeight: 0 }}>{chainLogo(draft.chain, 26)}</div>
        </div>
        <div style={{ ...SECTION, marginBottom: 7 }}>{t('wr_address')}</div>
        <input
          value={draft.address}
          onChange={e => setDraft(d => ({ ...d, address: e.target.value }))}
          placeholder={isUtxo ? t('wr_address_ph_utxo') : t('wr_address_ph')}
          autoCapitalize="off" autoCorrect="off" spellCheck={false}
          style={{ ...inputBase, fontFamily: F.mono, fontSize: 12, borderColor: draft.address && !addrOk ? T.red : (T.line || T.border) }}
        />
        <div style={{ ...SUB, fontSize: 12, marginTop: 8 }}>{t('wr_addr_help')}{isUtxo ? ' ' + t('wr_addr_help_xpub') : ''}</div>
        {error && <div style={{ marginTop: 14 }}><InfoBanner tone="error" title={t('con_error')}>{error}</InfoBanner></div>}
        <Tap onClick={save} disabled={!addrOk || busy} style={{
          marginTop: 20, width: '100%', padding: '15px', background: addrOk && !busy ? T.redGradient : T.line,
          color: T.white, border: 'none', borderRadius: R.md, fontFamily: F.family, fontSize: 15, fontWeight: 700,
        }}>{busy ? t('wr_saving') : t('wr_add')}</Tap>
      </>
    );
  } else {
    body = (
      <motion.div variants={STAGGER} initial="hidden" animate="show">
        <motion.div variants={ITEM}>
          <InfoBanner tone="info" title={t('wr_how_title')}>{t('wr_how_body')}</InfoBanner>
        </motion.div>

        {/* „Zu prüfen" — vom Watch-Cron entdeckte Bewegungen, je mit Typ-Badge */}
        {candidates.length > 0 && (
          <motion.div variants={ITEM} style={{ marginTop: 18 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
              <span style={{ fontFamily: F.display, fontSize: 17, fontWeight: 700, color: T.text, letterSpacing: '-0.02em' }}>{t('wr_review_title')}</span>
              <span style={{ color: T.red, fontWeight: 800 }}>·</span>
              <span style={{ fontSize: 13, color: T.sub, fontFamily: F.family }}>{candidates.length}</span>
            </div>
            <div style={{ ...SUB, fontSize: 12.5, marginBottom: 11 }}>{t('wr_review_hint')}</div>
            <div style={{ border: `1px solid ${T.line || T.border}`, borderRadius: R.md, overflow: 'hidden', background: T.surface, boxShadow: T.cardInset }}>
              {candidates.slice(0, 8).map((c, i) => (
                <Tap key={c.id} onClick={openReview} scale={0.99} style={{
                  display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                  padding: '12px 14px', background: 'none', border: 'none', color: T.text,
                  borderTop: i ? `1px solid ${T.lineSoft || T.line}` : 'none',
                }}>
                  <CoinImage sym={c.coin} marketData={marketData} size={30} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: T.text, fontFamily: F.family }}>{c.coin || '—'}</span>
                      <TypeBadge typ={c.typ} />
                    </div>
                    <div style={{ fontSize: 11, color: T.sub, fontFamily: F.family, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(c.walletLabel || c.chain || '')}{c.datum ? ` · ${c.datum}` : ''}
                    </div>
                  </div>
                  {c.menge != null && (
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: T.text, fontFamily: F.mono, flexShrink: 0 }}>{c.menge}</span>
                  )}
                </Tap>
              ))}
            </div>
            <Tap onClick={openReview} disabled={reviewLoading} scale={0.985} style={{
              marginTop: 11, width: '100%', padding: '13px', background: T.redGradient,
              color: T.white, border: 'none', borderRadius: R.md, fontFamily: F.family, fontSize: 14, fontWeight: 700,
              opacity: reviewLoading ? 0.7 : 1,
            }}>{reviewLoading ? '…' : t('wr_review_open', { n: candidates.length })}</Tap>
          </motion.div>
        )}
        <motion.div variants={ITEM}>
          <Tap onClick={openWizard} scale={0.985} style={{
            marginTop: 18, width: '100%', padding: '24px', borderRadius: R.md,
            border: `1.5px dashed ${T.line || T.border}`, background: 'transparent', color: T.text,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            fontFamily: F.family, fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
            {t('wr_add_cta')}
          </Tap>
        </motion.div>
        <motion.div variants={ITEM} style={{ marginTop: 26 }}>
          <div style={{ ...SECTION, marginBottom: 12 }}>{t('wr_saved', { n: wallets.length })}</div>
          {loading ? <div style={SUB}>…</div>
            : wallets.length === 0 ? <div style={SUB}>{t('wr_empty')}</div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {(() => {
                  // Nach Anbieter GRUPPIEREN (aufklappbares Register): „Ledger" auf →
                  // alle Ledger-Wallets, „Phantom" auf → alle Phantom-Wallets usw.
                  const groups = {};
                  for (const ww of wallets) { const p = providerOf(ww); (groups[p] = groups[p] || []).push(ww); }
                  const names = Object.keys(groups).sort((a, b) => a === PROVIDER_OTHER ? 1 : b === PROVIDER_OTHER ? -1 : groups[b].length - groups[a].length);
                  return names.map(prov => {
                    const glist = groups[prov];
                    const gopen = !!openGroups[prov];
                    return (
                    <div key={prov} style={{ border: `1px solid ${T.line || T.border}`, borderRadius: R.md, background: T.surface, overflow: 'hidden', boxShadow: T.cardInset }}>
                      {/* Gruppen-Kopf: Anbieter-Logo + Name + Anzahl + Chevron (auf/zu) */}
                      <Tap onClick={() => setOpenGroups(g => ({ ...g, [prov]: !g[prov] }))} scale={0.995} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '13px 15px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                        {prov !== PROVIDER_OTHER
                          ? <ExchangeLogo name={prov} size={30} />
                          : <span style={{ width: 30, height: 30, borderRadius: 8, background: T.surfaceHi || T.surface, border: `1px solid ${T.line || T.border}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: T.sub, flexShrink: 0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/><path d="M3 7h16a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H3"/><circle cx="16" cy="12" r="1.3" fill="currentColor" stroke="none"/></svg></span>}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: F.family, letterSpacing: '-0.01em' }}>{prov}</div>
                          <div style={{ fontSize: 11, color: T.sub, fontFamily: F.family, marginTop: 2 }}>{t('wr_saved', { n: glist.length })}</div>
                        </div>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: gopen ? 'rotate(180deg)' : 'none', transition: 'transform .2s ease' }}><path d="M6 9l6 6 6-6"/></svg>
                      </Tap>
                      {gopen && glist.map(w => {
                  const provider = providerOf(w);
                  return (
                  <div key={w.id} style={{
                    display: 'flex', flexDirection: 'column', padding: '12px 15px',
                    borderTop: `1px solid ${T.lineSoft || T.line}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                      {/* Asset-Coin-Icon (echtes, farbiges Logo) + Anbieter-Icon nebeneinander */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
                        <CoinImage sym={chainSym(w.chain)} marketData={marketData} size={36} />
                        {provider ? (
                          <span title={provider} style={{ lineHeight: 0 }}><ExchangeLogo name={provider} size={26} /></span>
                        ) : (
                          <span title={t('wr_self_custody') || 'Self-custody'} style={{
                            width: 26, height: 26, borderRadius: 8, flexShrink: 0,
                            background: T.surfaceHi || T.surface, border: `1px solid ${T.line || T.border}`,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: T.sub,
                          }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /><path d="M3 7h16a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H3" /><circle cx="16" cy="12" r="1.3" fill="currentColor" stroke="none" /></svg>
                          </span>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14.5, fontWeight: 700, color: T.text, fontFamily: F.family, letterSpacing: '-0.01em' }}>{w.label}</div>
                        <div style={{ fontSize: 11, color: T.sub, fontFamily: F.mono, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {(provider ? `${provider} · ` : '') + (chainOf(w.chain).name || w.chain)} · {w.address}
                        </div>
                      </div>
                      <Tap onClick={() => handleDelete(w.id)} scale={0.86} hoverScale={1.12} title={t('delete')} style={{ flexShrink: 0, background: 'none', border: 'none', color: T.sub, padding: 6, lineHeight: 0 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></svg>
                      </Tap>
                    </div>
                    {/* „Eigene Wallet"-Toggle entfernt: alles unter „Meine Wallets" IST
                        per Definition eine eigene Wallet (die Eigen-Transfer-Erkennung
                        nutzt ohnehin jede registrierte Adresse). Bleibt nur die
                        Überwachungs-Option. */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.lineSoft || T.line}` }}>
                      <Toggle on={!!w.autoWatch} onClick={() => toggleWatch(w.id, 'autoWatch')} label={t('wr_watch')} sub={t('wr_watch_sub')} />
                    </div>
                  </div>
                  );
                      })}
                    </div>
                    );
                  });
                })()}
              </div>
            )}
        </motion.div>
      </motion.div>
    );
  }

  // „Zu prüfen": ein Kandidat wurde geöffnet → vorhandener ImportReviewScreen
  // übernimmt den Screen. onImport committet (App.handleImportCsv → addBatch),
  // danach werden die übernommenen Kandidaten entfernt.
  if (reviewResult) {
    return (
      <ImportReviewScreen
        result={reviewResult}
        fileName={t('wr_review_src')}
        uid={uid}
        existingTxs={txs}
        onImport={async (clean, opts) => {
          await onImport?.(clean, opts);
          await clearCandidates(uid, reviewResult._candIds || []);
          setCandidates([]);
          setReviewResult(null);
        }}
        onCancel={() => setReviewResult(null)}
      />
    );
  }

  return (
    <div style={PAGE}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        {!(isNativeApp && step === null) && (
          <Tap onClick={back} scale={0.86} style={{ background: 'none', border: 'none', padding: 4, color: T.text, lineHeight: 0 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          </Tap>
        )}
        <div style={H1}>{title}</div>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={step || 'list'} initial={SLIDE.initial} animate={SLIDE.animate} exit={SLIDE.exit} transition={SLIDE.transition}>
          {body}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
